import { Load, MultiLegShippingEmissionEstimate, ShippingRoute } from '@lune-climate/lune'
import { getFormattedAmount, Leg, Legs, RequestAndResponse } from '@lune-fe/lune-components-lib'
import { Text } from '@lune-fe/lune-ui-lib'
import { useMemo, useState } from 'react'

import Divider from 'components/Divider'
import useIsTestMode from 'hooks/useIsTestMode'
import { getTestApiKey } from 'utils'
import MultiLegItem from 'views/CalculateEmissions/MultiLegLogistics/MultiLegItem'

const MultiLegEstimate = ({
    legs,
    load,
    meta,
    requestRoute,
    source,
    pdfStructure,
}: {
    legs: Leg[]
    load: Load
    meta?: any
    requestRoute?: ShippingRoute
    source?: MultiLegShippingEmissionEstimate['source']
    pdfStructure?: boolean
}) => {
    const [activeTabIndex, setActiveTabIndex] = useState<string>('1')

    const activeLeg = useMemo(() => {
        if (activeTabIndex && legs.length) {
            return legs[Number(activeTabIndex) - 1]
        }
        return null
    }, [activeTabIndex, legs])

    const isTestMode = useIsTestMode()
    const testApiKey = getTestApiKey()

    const legFormattedAmount = (leg: Leg) => getFormattedAmount(leg.mass.amount)

    const activeLegFormattedAmount = useMemo(() => {
        if (activeTabIndex && legs.length) {
            const leg = legs[Number(activeTabIndex) - 1]
            return legFormattedAmount(leg)
        }
        return null
    }, [activeTabIndex, legs])

    return (
        <>
            {!pdfStructure && (
                <Legs
                    legs={legs}
                    activeTabIndex={Number(activeTabIndex)}
                    setActiveTab={(activeIndex) => setActiveTabIndex(activeIndex.toString())}
                />
            )}
            {!pdfStructure && activeLeg && activeLegFormattedAmount && legs.length && (
                <>
                    <MultiLegItem
                        pdfStructure={pdfStructure}
                        requestRoute={requestRoute}
                        activeLegFormattedAmount={activeLegFormattedAmount}
                        activeLeg={activeLeg}
                        activeTabIndex={activeTabIndex}
                        load={load}
                    />
                    <Divider />
                    <RequestAndResponse
                        path={`/v1/estimates/shipping/multi-leg`}
                        request={meta.response.request}
                        response={meta.response}
                        source={source}
                        idempotencyKey={
                            (meta.response as { idempotency_key?: string }).idempotency_key
                        }
                        metadata={
                            (
                                meta.response as {
                                    metadata?: Record<string, string>
                                }
                            ).metadata
                        }
                        isTestMode={isTestMode}
                        testApiKey={testApiKey}
                    />
                </>
            )}
            {pdfStructure && legs.length && (
                <>
                    {legs.map((leg, index) => {
                        const legAmount = legFormattedAmount(leg)
                        return (
                            <>
                                <Divider />
                                <Text variant={'h5'}>
                                    Leg {index + 1}: {legAmount.displayAmount}{' '}
                                    {legAmount.amountUnit}CO₂e
                                </Text>
                                <MultiLegItem
                                    pdfStructure={true}
                                    requestRoute={requestRoute}
                                    activeLegFormattedAmount={legAmount}
                                    activeLeg={leg}
                                    activeTabIndex={(index + 1).toString()}
                                    load={load}
                                />
                            </>
                        )
                    })}
                </>
            )}
        </>
    )
}

export default MultiLegEstimate
